// Tiny hash-based router. Bookmarkable, no extra dependency.
// Module-level $state requires a .svelte.ts file.

interface ParsedHash {
  path: string;
  query: Record<string, string>;
}

function parseHash(): ParsedHash {
  const raw = window.location.hash;
  let h = raw.startsWith("#") ? raw.slice(1) : raw;
  if (!h || h === "/") return { path: "/", query: {} };
  const qIdx = h.indexOf("?");
  let path = h;
  const query: Record<string, string> = {};
  if (qIdx >= 0) {
    path = h.slice(0, qIdx);
    const search = h.slice(qIdx + 1);
    for (const pair of search.split("&")) {
      if (!pair) continue;
      const eq = pair.indexOf("=");
      if (eq < 0) {
        query[decodeURIComponent(pair)] = "";
      } else {
        query[decodeURIComponent(pair.slice(0, eq))] = decodeURIComponent(
          pair.slice(eq + 1),
        );
      }
    }
  }
  if (!path) path = "/";
  return { path, query };
}

class Router {
  current = $state(parseHash().path);
  query = $state<Record<string, string>>(parseHash().query);
  // Dynamic path-params extracted by findRoute against the matched
  // route definition (e.g. "/infra/cluster/:host_id" matched against
  // "/infra/cluster/macbook" → { host_id: "macbook" }). Pages read
  // their params from here. App.svelte writes them whenever the
  // route changes; pages should treat the value as read-only.
  params = $state<Record<string, string>>({});

  constructor() {
    window.addEventListener("hashchange", () => {
      const p = parseHash();
      this.current = p.path;
      this.query = p.query;
    });
  }
}

export const router = new Router();

export function navigate(path: string): void {
  window.location.hash = path;
}

/** Zieht den Router auf die Adresszeile nach. Nötig nach einem
 *  replaceState von außen: der feuert kein hashchange. */
export function leseHashNeu(): void {
  const p = parseHash();
  router.current = p.path;
  router.query = p.query;
}

// Liest einen Query-Param und entfernt ihn anschliessend aus der URL.
// Pages benutzen das fuer ?q=<filter> aus der TopBar-Suche: der
// Filter wird einmalig in den lokalen State uebernommen, der Param
// fliegt aus der URL. Damit bleibt ein manuell geleerter Filter
// auch nach Reload leer (sonst wuerde der Seed-Effect ihn wieder
// auffuellen). replaceState statt navigate, damit kein zusaetzlicher
// History-Eintrag entsteht und der Scroll nicht springt.
export function consumeQueryParam(key: string): string | undefined {
  const value = router.query[key];
  if (value === undefined) return undefined;

  const hash = window.location.hash;
  const rawPath = hash.startsWith("#") ? hash.slice(1) : hash;
  const qIdx = rawPath.indexOf("?");
  if (qIdx >= 0) {
    const pathOnly = rawPath.slice(0, qIdx);
    const search = rawPath.slice(qIdx + 1);
    const parts: string[] = [];
    for (const pair of search.split("&")) {
      if (!pair) continue;
      if (pair === key || pair.startsWith(key + "=")) continue;
      parts.push(pair);
    }
    const newPath = parts.length ? `${pathOnly}?${parts.join("&")}` : pathOnly;
    window.history.replaceState(null, "", "#" + newPath);
  }

  // hashchange feuert nicht bei replaceState -- router.query selbst syncen.
  const next = { ...router.query };
  delete next[key];
  router.query = next;

  return value;
}

export function isActive(path: string, current: string): boolean {
  if (path === current) return true;
  // Treat / as exact-match only.
  if (path === "/") return false;
  // Section-active: /infra is active if current is /infra/cluster.
  return current.startsWith(path + "/");
}
